<script setup lang="ts">
import type { File, FileTag } from '~/types/api'

const props = defineProps<{
  file: File
  allFileTags?: FileTag[]
  inline?: boolean
}>()

const { getResourceUrl } = useResources()

const fileImageSrc = '/assets/front/images/icons/file.svg'

const fileExtension = computed((): string => {
  return props.file.fileName?.split('.').pop() ?? ''
})

const formattedSize = computed((): string => {
  let remainingSize = Number(props.file.fileSize ?? '0')
  let result = ''
  let level = 1
  const maxLevel = 1
  const units = {
    TB: 1099511627776,
    GB: 1073741824,
    MB: 1048576,
    kB: 1024,
    B: 1,
  }
  for (const [unit, inBytes] of Object.entries(units)) {
    if (remainingSize >= inBytes) {
      const unitSize = remainingSize / inBytes
      const intUnitSize = parseInt(unitSize.toString())
      result +=
        (level >= maxLevel
          ? +(Math.round(+(unitSize + 'e+1')) + 'e-1')
          : intUnitSize) +
        unit +
        ' '
      remainingSize -= intUnitSize * inBytes
      if (level++ >= maxLevel) {
        break
      }
    }
  }
  return result.trim()
})

const fileTags = computed(() => {
  const result = [] as FileTag[]
  if (props.inline) {
    return result
  }
  ;(props.file.fileTags || []).forEach((fileTag: FileTag) => {
    const found = props.allFileTags.find(
      (cur: FileTag) => cur.translationOfNid === fileTag.nid
    )
    if (found !== undefined) {
      result.push(found)
    }
  })
  return result
})

const showThumbnail = computed(() => {
  return props.file.imageRatio !== null || fileExtension.value === 'pdf'
})

function downloadFile() {
  if (!props.file.originalUrl) {
    return
  }

  const a = window.document.createElement('a')
  a.href = getResourceUrl(props.file.originalUrl)
  a.download = props.file.originalFileName ?? ''
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const emit = defineEmits(['showImage'])
</script>

<template>
  <article v-if="!inline" class="mb-0 px-8 text-center">
    <div
      class="relative block w-full overflow-hidden before:block before:pt-[100%]"
      :class="{
        'bg-neutral-50': !showThumbnail,
      }"
    >
      <UiPhotoThumbnail
        @click="() => emit('showImage')"
        v-if="showThumbnail"
        class="absolute left-0 top-0 flex h-full w-full items-center justify-center"
        :image="file"
        contain-image
      />
      <NuxtLink
        v-else
        :to="getResourceUrl(file.originalUrl)"
        @click.prevent="downloadFile()"
      >
        <div
          class="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center object-contain"
        >
          <img
            :height="file.imageHeight || 300"
            :width="file.imageWidth || 300"
            :src="getResourceUrl(fileImageSrc)"
            loading="lazy"
            class="my-2 inline-block h-12 w-12 object-contain align-middle"
          />
          <strong class="inline-block uppercase">
            {{ fileExtension }}
          </strong>
        </div>
      </NuxtLink>
    </div>

    <div class="mt-8">
      <h2 class="mb-4 truncate text-xl font-bold">
        <UTooltip
          :text="file.originalUrl"
          :popper="{ placement: 'top' }"
          :ui="{
            wrapper: 'relative inline',
            color: 'text-white',
            background: 'bg-black',
            rounded: 'rounded-none',
            base: '[@media(pointer:coarse)]:hidden h-6 px-3 py-3 text-sm font-normal break-normal whitespace-normal h-fit after:absolute after:left-1/2 after:top-[100%] after:-translate-x-1/2 after:border-8 after:border-x-transparent after:border-b-transparent after:border-t-black',
          }"
        >
          <NuxtLink
            :to="getResourceUrl(file.originalUrl)"
            :title="file.originalUrl"
            @click.prevent="downloadFile()"
          >
            {{ file.originalFileName }}
          </NuxtLink>
        </UTooltip>
      </h2>
      <div
        v-if="fileTags.length !== 0 && !props.inline"
        class="mb-2 flex justify-center gap-2"
      >
        <small
          v-for="fileTag in fileTags"
          :key="fileTag.nid"
          class="inline-block whitespace-nowrap bg-black px-3 py-1.5 text-center text-sm text-white"
        >
          {{ fileTag.title }}
        </small>
      </div>

      <samp v-if="file.fileSize" class="text-truncate text-xs text-neutral-500">
        {{ formattedSize }}
      </samp>
    </div>
  </article>
  <article v-else>
    <NuxtLink
      class="my-1 flex appearance-none flex-wrap items-baseline gap-2 px-2 no-underline hover:bg-neutral-100"
      :to="getResourceUrl(file.originalUrl)"
      @click.prevent="downloadFile()"
    >
      <div>
        <img
          :height="file.imageHeight || 300"
          :width="file.imageWidth || 300"
          :src="getResourceUrl(fileImageSrc)"
          loading="lazy"
          class="my-2 inline-block w-6 object-contain align-middle"
        />
      </div>
      <UTooltip
        :text="file.originalFileName"
        class="max-w-52"
        :popper="{ placement: 'top' }"
        :ui="{
          wrapper: 'relative inline',
          color: 'text-white',
          background: 'bg-black',
          rounded: 'rounded-none',
          base: '[@media(pointer:coarse)]:hidden h-6 px-3 py-3 text-sm font-normal break-normal whitespace-normal h-fit after:absolute after:left-1/2 after:top-[100%] after:-translate-x-1/2 after:border-8 after:border-x-transparent after:border-b-transparent after:border-t-black',
        }"
      >
        <div
          class="overflow-x-hidden text-ellipsis whitespace-nowrap font-bold"
        >
          {{ file.originalFileName }}
        </div>
      </UTooltip>
      <div class="ml-auto flex items-baseline justify-end gap-2">
        <div class="text-center uppercase">
          {{ fileExtension }}
        </div>
        <samp
          v-if="file.fileSize"
          class="text-truncate text-xs text-neutral-500"
        >
          {{ formattedSize }}
        </samp>
      </div>
    </NuxtLink>
  </article>
</template>

<style scoped></style>
